<div class="mt-6 w-full flex flex-col gap-5">
  <p-toast></p-toast>

  <p-card>
    <ng-template #title>
      <h1>
        {{ 'label.psas' | translate }}
      </h1>
    </ng-template>

    <div class="flex justify-between gap-2 mb-5">
      <p-iconfield>
        <p-inputicon styleClass="fa fa-search" />
        <input
          pInputText
          type="text"
          (input)="onGlobalSearch($any($event.target).value)"
          [placeholder]="'label.search' | translate"
        />
      </p-iconfield>
      <div>
        <p-button
          (click)="updateCreateMenuLabels(); createMenu.toggle($event)"
          [label]="'psa.add' | translate"
        />
        <p-menu #createMenu [model]="createMenuItems" [popup]="true" />
      </div>
    </div>

    <p-table
      #psaTable
      [value]="psas()"
      stripedRows
      showGridlines
      dataKey="id"
      [resizableColumns]="true"
      columnResizeMode="expand"
      [paginator]="true"
      [rows]="25"
      [rowsPerPageOptions]="[25, 50, 75, 100]"
      sortField="id"
      [sortOrder]="1"
      [scrollable]="true"
      [loading]="false"
      [tableStyle]="{ 'min-width': '30rem' }"
      [globalFilterFields]="globalFilterFields"
      [expandedRowKeys]="expandedRowKeys()"
    >
      <ng-template #header>
        <tr>
          <th style="width: 3rem"></th>
          @for (column of psaColumns; track $index) {
            <th
              [pSortableColumn]="column.sortField ?? ''"
              [pSortableColumnDisabled]="!column.sortField"
              pResizableColumn
            >
              {{ column.header | translate }}
              @if (column.sortField) {
                <p-sortIcon [field]="column.sortField" />
              }
              @if (column.filterField) {
                @if (column.matchMode === 'in') {
                  <p-columnFilter
                    [field]="column.filterField"
                    matchMode="in"
                    display="menu"
                    [showOperator]="false"
                    [showMatchModes]="false"
                    [showAddButton]="false"
                  >
                    <ng-template #filter let-value let-filter="filterCallback">
                      <p-multiselect
                        [ngModel]="value"
                        [options]="filterOptions()[column.field]"
                        (onChange)="filter($event.value)"
                        optionLabel="label"
                        optionValue="value"
                        [maxSelectedLabels]="1"
                        class="w-full"
                      />
                    </ng-template>
                  </p-columnFilter>
                } @else if (column.matchMode === 'between') {
                  <p-columnFilter
                    [field]="column.filterField"
                    matchMode="between"
                    display="menu"
                    [showOperator]="false"
                    [showMatchModes]="false"
                    [showAddButton]="false"
                  >
                    <ng-template #filter let-value let-filter="filterCallback">
                      <p-datepicker
                        [ngModel]="value"
                        (ngModelChange)="filter($event)"
                        selectionMode="range"
                        dateFormat="dd.mm.yy"
                        [readonlyInput]="true"
                        appendTo="body"
                      />
                    </ng-template>
                  </p-columnFilter>
                } @else {
                  <p-columnFilter
                    [field]="column.filterField"
                    display="menu"
                    [type]="column.type"
                    [matchMode]="column.matchMode"
                    [showOperator]="column.showOperator || false"
                    [showMatchModes]="column.showMatchModes || false"
                    [showAddButton]="column.showAddButton || false"
                  ></p-columnFilter>
                }
              }
            </th>
          }
          <th></th>
        </tr>
      </ng-template>
      <ng-template #body let-psa>
        <tr>
          <td style="width: 3rem">
            <p-button
              type="button"
              [text]="true"
              severity="secondary"
              [rounded]="true"
              (onClick)="toggleRow(psa)"
              [icon]="
                isExpanded(psa) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'
              "
              [pTooltip]="'psa.toggleDetails' | translate"
              tooltipPosition="top"
              [attr.aria-expanded]="isExpanded(psa)"
              [attr.aria-label]="'psa.toggleDetails' | translate"
            />
          </td>
          <td>{{ psa.id }}</td>
          <td>
            {{
              psa.type === 'adult'
                ? ('psa.typeAdult' | translate)
                : ('psa.typeChild' | translate)
            }}
          </td>
          <td>
            {{ psa.tags?.join(', ') }}
          </td>
          <td>{{ psa.manufacturer_number_set || '-' }}</td>
          <td>
            {{
              psa.last_checked_at
                ? (psa.last_checked_at | date: 'dd.MM.yyyy HH:mm' : 'Europe/Berlin')
                : '-'
            }}
          </td>
          <td>
            <div class="flex items-center justify-start gap-2">
              <!--
                Fixed-width slot: keeps the action icons aligned across rows
                whether or not this PSA has open defects.
              -->
              <div class="flex w-8 shrink-0 justify-center">
                @if (psa.open_defect_count > 0) {
                  <button
                    type="button"
                    class="cursor-pointer rounded-full border-none bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2"
                    (click)="navigateToOpenDefects(psa)"
                    [pTooltip]="'psa.openDefects' | translate"
                    tooltipPosition="top"
                    [attr.aria-label]="
                      ('psa.openDefects' | translate) +
                      ': ' +
                      psa.open_defect_count
                    "
                  >
                    <p-badge
                      [value]="psa.open_defect_count"
                      severity="danger"
                    />
                  </button>
                }
              </div>
              <button
                pButton
                type="button"
                icon="fa fa-pencil"
                (click)="openEditDialog(psa)"
                text
                rounded
                severity="secondary"
                [pTooltip]="'psa.edit' | translate"
                tooltipPosition="top"
              ></button>
              <button
                pButton
                type="button"
                icon="fa fa-triangle-exclamation"
                (click)="navigateToCreateDefect(psa)"
                text
                rounded
                severity="warn"
                [pTooltip]="'psa.reportDefect' | translate"
                tooltipPosition="top"
              ></button>
              <button
                pButton
                type="button"
                icon="fa fa-clock-rotate-left"
                (click)="navigateToDefects(psa)"
                [disabled]="!psa.has_defects"
                text
                rounded
                severity="secondary"
                [pTooltip]="'psa.defectHistory' | translate"
                tooltipPosition="top"
              ></button>
              <button
                pButton
                type="button"
                icon="fa fa-clipboard-list"
                (click)="navigateToInspections(psa)"
                text
                rounded
                severity="secondary"
                [pTooltip]="'psa.inspectionHistory' | translate"
                tooltipPosition="top"
              ></button>
              <button
                pButton
                type="button"
                icon="fa fa-trash"
                (click)="onRowDeleteInit($event, psa)"
                text
                rounded
                severity="secondary"
                [pTooltip]="'label.delete' | translate"
                tooltipPosition="top"
              ></button>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template #expandedrow let-psa>
        <tr>
          <td [attr.colspan]="detailColspan" class="p-6">
            <h3 class="mb-3">{{ 'psa.manufacturerNumbers' | translate }}</h3>
            <dl class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
              <dt class="opacity-70">
                {{ 'psa.manufacturerNumberSet' | translate }}
              </dt>
              <dd>{{ psa.manufacturer_number_set || '-' }}</dd>
              <dt class="opacity-70">
                {{ 'psa.manufacturerNumberHarness' | translate }}
              </dt>
              <dd>{{ psa.manufacturer_number_harness || '-' }}</dd>
              <dt class="opacity-70">
                {{ 'psa.manufacturerNumberCarabiners' | translate }}
              </dt>
              <dd>{{ psa.manufacturer_number_carabiners || '-' }}</dd>
            </dl>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <p-confirmdialog />

    <p-dialog
      [header]="
        editingPsa()
          ? ('psa.editHeader' | translate)
          : ('psa.createHeader' | translate)
      "
      [modal]="true"
      [visible]="dialogVisible()"
      (visibleChange)="$event ? null : closeDialog()"
      styleClass="w-[90vw] max-w-xl"
    >
      <form [formGroup]="numberForm" (ngSubmit)="saveDialog()">
        <div class="flex gap-8 mb-5">
          <div class="flex flex-col gap-1">
            <span class="text-sm opacity-70">{{ 'psa.id' | translate }}</span>
            <span>{{
              editingPsa()?.id ?? ('psa.idAutoGenerated' | translate)
            }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm opacity-70">{{ 'psa.type' | translate }}</span>
            <span>{{
              dialogType() === 'adult'
                ? ('psa.typeAdult' | translate)
                : ('psa.typeChild' | translate)
            }}</span>
          </div>
        </div>

        <h3 class="mb-3">{{ 'psa.manufacturerNumbers' | translate }}</h3>

        <div class="flex flex-col gap-4 mb-5">
          @for (field of numberFields; track field.key) {
            <div class="flex flex-col gap-2">
              <label [for]="field.key">{{ field.label | translate }}</label>
              <input
                pInputText
                type="text"
                [id]="field.key"
                [formControlName]="field.key"
                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid(field.key) }"
                class="w-full"
              />
              @if (isInvalid(field.key)) {
                <small class="text-red-500">{{
                  errorKey(field.key) | translate
                }}</small>
              }
            </div>
          }
        </div>

        <div class="flex justify-end gap-2">
          <p-button
            [label]="'label.cancel' | translate"
            severity="secondary"
            [disabled]="saving()"
            (click)="closeDialog()"
          />
          <p-button
            [label]="'label.save' | translate"
            type="submit"
            [disabled]="saving()"
          />
        </div>
      </form>
    </p-dialog>
  </p-card>
</div>
